import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";

const normalizeLedgerResponse = (result) => {
  if (!result || result.errors) {
    return {
      list: [],
      total: 0,
      totalPages: 0,
      currentPage: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };
  }
  const meta = result.meta || {};
  const total = result.total ?? meta.total ?? 0;
  const totalPages =
    total === 0 ? 0 : Math.max(1, result.totalPages ?? meta.totalPages ?? 1);
  const currentPage = result.currentPage ?? meta.page ?? 1;
  const hasNextPage =
    result.hasNextPage ?? (total > 0 && currentPage < totalPages);
  const hasPrevPage = result.hasPrevPage ?? currentPage > 1;
  return {
    list: Array.isArray(result.data) ? result.data : [],
    total,
    totalPages,
    currentPage,
    hasNextPage,
    hasPrevPage,
  };
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const JVLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall } = useApi();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);

  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [remarks, setRemarks] = useState("");

  const [limitVal, setLimitVal] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await AsyncGetApiCall("/users/dynamic-list", {
        limitVal: 1000,
      });
      if (res?.success && Array.isArray(res.data)) {
        setAccounts(res.data);
      }
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  }, [AsyncGetApiCall]);

  const fetchTransactions = useCallback(
    async (page, limit) => {
      setTableLoading(true);
      try {
        const result = await AsyncGetApiCall("/accounts/my-cash-ledger", {
          pageNo: page,
          limitVal: limit,
        });
        if (result?.success) {
          const norm = normalizeLedgerResponse(result);
          setTransactions(norm.list);
          setTotal(norm.total);
          setTotalPages(norm.totalPages);
          setCurrentPage(norm.currentPage);
          setHasNextPage(norm.hasNextPage);
          setHasPrevPage(norm.hasPrevPage);
        } else {
          setTransactions([]);
          setTotal(0);
          setTotalPages(0);
          setCurrentPage(1);
          setHasNextPage(false);
          setHasPrevPage(false);
        }
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setTableLoading(false);
      }
    },
    [AsyncGetApiCall],
  );

  useEffect(() => {
    fetchAccounts();
    fetchTransactions(1, limitVal);
  }, [fetchAccounts, fetchTransactions, limitVal]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchTransactions(newPage, limitVal);
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setLimitVal(newLimit);
    fetchTransactions(1, newLimit);
  };

  const pageButtons = useMemo(() => {
    const n = totalPages;
    if (n <= 0) return [];
    const cap = Math.min(n, 5);
    const out = [];
    for (let i = 0; i < cap; i += 1) {
      let pageNum;
      if (n <= 5) pageNum = i + 1;
      else if (currentPage <= 3) pageNum = i + 1;
      else if (currentPage >= n - 2) pageNum = n - 4 + i;
      else pageNum = currentPage - 2 + i;
      out.push(pageNum);
    }
    return out;
  }, [totalPages, currentPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromAccount || !toAccount || amount === "" || amount <= 0) {
      toast.error("Please fill all required fields");
      return;
    }

    if (fromAccount === toAccount) {
      toast.error("From and To accounts cannot be the same");
      return;
    }

    const fromAcc = accounts.find((a) => a._id === fromAccount);
    const toAcc = accounts.find((a) => a._id === toAccount);

    if (!fromAcc || !toAcc) return;

    setLoading(true);
    try {
      const res = await AsyncPostApiCall("/accounts/create-jv", {
        fromUserId: fromAccount,
        fromUserType: fromAcc.role || "user",
        toUserId: toAccount,
        toUserType: toAcc.role || "user",
        amount: Number(amount),
        date,
        remarks,
      });

      if (res?.success) {
        toast.success(res.message || "JV Transfer completed");
        setFromAccount("");
        setToAccount("");
        setAmount("");
        setRemarks("");
        fetchTransactions(1, limitVal);
      } else {
        toast.error(res?.errors?.[0] || res?.message || "Transfer failed");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  console.log('fromAccount', fromAccount)
  console.log('toAccount', toAccount)


  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <h6 className="fw-semibold mb-0">JV Entry</h6>
        <ul className="d-flex align-items-center gap-2">
          <li className="fw-medium">
            <a
              href="/dashboard"
              className="d-flex align-items-center gap-1 hover-text-primary"
            >
              <Icon
                icon="solar:home-smile-angle-outline"
                className="icon text-lg"
              />
              Dashboard
            </a>
          </li>
          <li>-</li>
          <li className="fw-medium">Accounts</li>
          <li>-</li>
          <li className="fw-medium">JV</li>
        </ul>
      </div>

      <div className="row g-4 mb-24">
        <div className="col-12">
          <div className="card shadow-sm radius-16 border">
            <div className="card-header border-bottom p-24">
              <h6 className="mb-0 text-lg">Create Transfer</h6>
            </div>
            <div className="card-body p-24">
              <form onSubmit={handleSubmit}>
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      From Account (Debit)
                    </label>
                    <div style={{ minWidth: "180px" }}>
                      <SearchableSelect
                        placeholder="Select Account"
                        value={fromAccount}
                        showSearch={true}
                        onChange={(e) => {
                          const value = e.target.value
                          if (value === toAccount) {
                            setFromAccount("")
                            toast.error("From and To account cannot be same")
                            return
                          }
                          setFromAccount(value)
                        }}
                        options={accounts.map((acc) => ({
                          label: `${acc.name} (${acc.loginId}) - ₹${(acc.currentBalance ?? 0).toLocaleString("en-IN")}`,
                          value: acc._id,
                        }))}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      To Account (Credit)
                    </label>
                    <div style={{ minWidth: "180px" }}>
                      <SearchableSelect
                        placeholder="Select Account"
                        value={toAccount}
                        showSearch={true}
                        onChange={(e) => {
                          const value = e.target.value
                          if (fromAccount === value) {
                            setToAccount("")
                            toast.error("From and To account cannot be same")
                            return
                          }
                          setToAccount(value)
                        }}
                        options={accounts.map((acc) => ({
                          label: `${acc.name} (${acc.loginId}) - ₹${(acc.currentBalance ?? 0).toLocaleString("en-IN")}`,
                          value: acc._id,
                        }))}
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Amount</label>
                    <div className="input-group">
                      <input
                        type="number"
                        min={0}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="form-control radius-8"
                        placeholder="0"
                        required
                      />
                      <span className="input-group-text bg-base radius-8">
                        ₹
                      </span>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="form-control radius-8"
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">Remarks</label>
                    <textarea
                      placeholder="Enter remarks..."
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="form-control radius-8"
                      rows={3}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="col-12 pt-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary radius-8 px-5 py-10 fw-semibold"
                    style={{ backgroundColor: "#043ba9", border: "none" }}
                  >
                    {loading ? (
                      <span className="d-flex align-items-center gap-2">
                        <Loader size={16} color="#fff" /> Processing...
                      </span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 ledger-table-card">
        <div className="card-header border-bottom p-24 d-flex justify-content-between align-items-center" style={{ zIndex: 10 }}>
          <h6 className="mb-0 text-lg">Recent Transactions</h6>
          <div className="d-flex align-items-center gap-2">
            <span className="text-secondary-light">Show</span>
            <SearchableSelect
              placeholder="Show"
              value={String(limitVal)}
              showSearch={false}
              onChange={handleLimitChange}
              options={PAGE_SIZE_OPTIONS.map((n) => ({
                label: String(n),
                value: String(n),
              }))}
            />
          </div>
        </div>

        <div className="table-responsive p-16">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Particulars</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Remark</th>
              </tr>
            </thead>
            <tbody
              style={{
                opacity: tableLoading ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              {tableLoading && transactions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <Loader size={24} color="#043ba9" />
                  </td>
                </tr>
              ) : transactions.length > 0 ? (
                transactions.map((tx) => (
                  <tr key={tx._id}>
                    <td>{formatDate(tx.date)}</td>
                    <td className="fw-medium">{tx.name}</td>
                    <td>
                      <span
                        className={`badge px-12 py-4 radius-4 text-xs ${tx.type === "transfer"
                          ? "bg-info-focus text-info-600"
                          : tx.type === "credit"
                            ? "bg-success-focus text-success-600"
                            : "bg-danger-focus text-danger-600"
                          }`}
                        style={{ textTransform: "uppercase" }}
                      >
                        {tx.type}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`fw-bold ${Number(tx.amount) === 0 || tx.type === "credit"
                          ? "text-success-600"
                          : "text-danger-600"
                          }`}
                      >
                        {tx.type === "debit" ? "-" : ""}₹
                        {Number(tx.amount).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                    <td className="text-secondary-light">
                      <span
                        style={{
                          display: "block",
                          maxWidth: "300px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tx.remark || "-"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-5 text-secondary-light"
                  >
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-24">
          <p className="mb-0 text-secondary-light text-sm">
            Showing{" "}
            <span className="fw-medium text-primary-light">
              {transactions.length > 0
                ? `${(currentPage - 1) * limitVal + 1} to ${Math.min(
                  currentPage * limitVal,
                  total,
                )} of ${total} entries`
                : "0 to 0 of 0 entries"}
            </span>
          </p>
          {totalPages > 0 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!hasPrevPage}
              >
                <Icon icon="tabler:chevron-left" className="icon" />
              </button>
              {pageButtons.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center ${pageNum === currentPage
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-base text-secondary-light"
                    }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="text-secondary-light px-1">…</span>
                  <button
                    type="button"
                    className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center bg-base text-secondary-light"
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!hasNextPage}
              >
                <Icon icon="tabler:chevron-right" className="icon" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JVLayer;
